import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../../components/common/DashboardLayout';
import AdminNav from '../../components/admin/AdminNav';
import * as affiliateService from '../../services/adminAffiliate.service';
import { getAdminMembers } from '../../services/admin.service';

const TYPE_BADGE = {
  affiliate: 'bg-reset-terracotta/10 text-reset-terracotta',
  royalty:   'bg-reset-sage/10 text-reset-sage',
};

export default function AdminAffiliates() {
  const [partners, setPartners] = useState(null);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [payoutFor, setPayoutFor] = useState(null); // partner being paid out

  const load = useCallback(async () => {
    try {
      setError(null);
      setPartners(await affiliateService.listPartners());
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load partners.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        <AdminNav />

        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="font-serif text-2xl font-bold text-reset-brown">Affiliates &amp; Royalties</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {partners ? `${partners.length} partner${partners.length === 1 ? '' : 's'}` : '—'}
            </p>
          </div>
          <button
            onClick={() => setShowForm(v => !v)}
            className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors"
          >
            {showForm ? 'Cancel' : '+ New Partner'}
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">{error}</div>
        )}

        {showForm && (
          <NewPartnerForm onCreated={() => { setShowForm(false); load(); }} onError={setError} />
        )}

        {!partners ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : partners.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm px-5 py-8 text-center text-sm text-gray-400">
            No partners yet.
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden divide-y divide-gray-50">
            {partners.map(p => (
              <div key={p.id} className="px-5 py-4 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link to={`/admin/affiliates/${p.id}`} className="text-sm font-semibold text-reset-brown hover:text-reset-terracotta hover:underline">{p.name}</Link>
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${TYPE_BADGE[p.partner_type]}`}>
                      {p.partner_type}
                    </span>
                    {!p.is_active && (
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Inactive</span>
                    )}
                    {p.awaiting_approval && (
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-reset-terracotta/10 text-reset-terracotta" title="They've submitted their intake form — their link/offer/page stay off until a Team365 admin approves">
                        Needs your approval
                      </span>
                    )}
                    {p.link_active === false && !p.awaiting_approval && (
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-reset-gold/25 text-reset-brown" title="Their link and $1 offer won't work until they finish their intake form">
                        Link not active — profile incomplete
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {p.email} · {p.commission_rate_pct}%
                    {p.referral_count != null ? ` · ${p.referral_count} referral${p.referral_count === 1 ? '' : 's'}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <div className="text-right">
                    <p className="text-sm font-semibold text-reset-brown">${Number(p.owed).toFixed(2)} owed</p>
                    <p className="text-xs text-gray-400">${Number(p.paid).toFixed(2)} paid</p>
                  </div>
                  <button
                    onClick={() => setPayoutFor(p)}
                    disabled={Number(p.owed) <= 0}
                    className="text-xs font-medium text-reset-terracotta hover:underline disabled:text-gray-300 disabled:no-underline"
                  >
                    Record payout
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {payoutFor && (
          <PayoutModal
            partner={payoutFor}
            onClose={() => setPayoutFor(null)}
            onSaved={() => { setPayoutFor(null); load(); }}
          />
        )}
      </div>
    </DashboardLayout>
  );
}

// ─── New partner form ────────────────────────────────────────────────────────

function NewPartnerForm({ onCreated, onError }) {
  const [members, setMembers] = useState([]);
  const [membersLoaded, setMembersLoaded] = useState(false);
  const [query, setQuery] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [email, setEmail] = useState('');
  const [partnerType, setPartnerType] = useState('affiliate');
  const [referralCode, setReferralCode] = useState('');
  const [commissionRatePct, setCommissionRatePct] = useState(10);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getAdminMembers().then(m => { setMembers(m?.members || []); setMembersLoaded(true); }).catch(() => setMembersLoaded(true));
  }, []);

  const matches = query.trim().length === 0 ? [] : members.filter(m =>
    (m.name || '').toLowerCase().includes(query.trim().toLowerCase()) ||
    (m.email || '').toLowerCase().includes(query.trim().toLowerCase())
  ).slice(0, 8);

  const pickMember = (m) => {
    setSelectedMember(m);
    setQuery(m.name);
    setEmail(m.email || '');
    setShowResults(false);
  };

  const clearMember = () => {
    setSelectedMember(null);
    setQuery('');
    setEmail('');
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedMember) {
      onError('Only existing members can become affiliates — pick one from the search results.');
      return;
    }
    setSaving(true);
    try {
      await affiliateService.createPartner({
        memberId: selectedMember.id,
        name: selectedMember.name, email: email.trim(), partnerType,
        referralCode: referralCode.trim(), commissionRatePct: Number(commissionRatePct),
      });
      onCreated();
    } catch (err) {
      onError(err.response?.data?.error || 'Could not create partner.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl shadow-sm p-5 mb-6 space-y-3">
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="relative">
          <label className="block text-xs font-medium text-gray-500 mb-1">Member</label>
          <input
            value={query}
            onChange={e => { setQuery(e.target.value); setSelectedMember(null); setShowResults(true); }}
            onFocus={() => setShowResults(true)}
            onBlur={() => setTimeout(() => setShowResults(false), 150)}
            placeholder={membersLoaded ? 'Search members by name or email…' : 'Loading members…'}
            required
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
          {selectedMember && (
            <p className="text-[11px] text-reset-sage mt-1">✓ Linked to existing member — {selectedMember.email}</p>
          )}
          {showResults && matches.length > 0 && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
              {matches.map(m => (
                <button type="button" key={m.id}
                  onMouseDown={() => pickMember(m)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-reset-cream border-b border-gray-50 last:border-0">
                  <span className="font-medium text-reset-brown">{m.name}</span>
                  <span className="text-gray-400 ml-2">{m.email}</span>
                </button>
              ))}
            </div>
          )}
          {showResults && query.trim().length > 0 && matches.length === 0 && membersLoaded && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm text-gray-400">
              No matching member. Only existing members can become affiliates.
            </div>
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
            disabled={!selectedMember}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta disabled:bg-gray-50 disabled:text-gray-400" />
        </div>
      </div>
      {selectedMember && (
        <button type="button" onClick={clearMember} className="text-xs text-gray-400 hover:text-reset-terracotta -mt-1">
          Change member
        </button>
      )}
      <div className="grid sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
          <select value={partnerType} onChange={e => setPartnerType(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta">
            <option value="affiliate">Affiliate (earns on their referrals)</option>
            <option value="royalty">Royalty (earns on every member)</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">
            Referral code {partnerType === 'royalty' && <span className="text-gray-400 font-normal">(not needed for royalty)</span>}
          </label>
          <input value={referralCode} onChange={e => setReferralCode(e.target.value)}
            required={partnerType === 'affiliate'}
            disabled={partnerType === 'royalty'}
            placeholder={partnerType === 'royalty' ? '—' : 'e.g. patti'}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta disabled:bg-gray-50 disabled:text-gray-400" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Commission %</label>
          <input type="number" step="0.01" value={commissionRatePct} onChange={e => setCommissionRatePct(e.target.value)} required
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
        </div>
      </div>
      <button type="submit" disabled={saving}
        className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50">
        {saving ? 'Creating…' : 'Create partner'}
      </button>
    </form>
  );
}

// ─── Payout modal ─────────────────────────────────────────────────────────────

function PayoutModal({ partner, onClose, onSaved }) {
  const [amount, setAmount] = useState(partner.owed);
  const [payoutDate, setPayoutDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await affiliateService.recordPayout(partner.id, { amount: Number(amount), payoutDate, note: note || undefined });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not record payout.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-50">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-lg p-5 w-full max-w-sm space-y-3">
        <h2 className="text-sm font-bold text-reset-brown">Record payout — {partner.name}</h2>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-xs">{error}</div>}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Amount</label>
          <input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} required
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Date</label>
          <input type="date" value={payoutDate} onChange={e => setPayoutDate(e.target.value)} required
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Note (optional)</label>
          <input value={note} onChange={e => setNote(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-500 hover:text-reset-brown">Cancel</button>
          <button type="submit" disabled={saving}
            className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50">
            {saving ? 'Saving…' : 'Record payout'}
          </button>
        </div>
      </form>
    </div>
  );
}
